<script setup lang="ts">
import { useTableStore } from '@/stores/tableStore.ts'
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue'
import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import ActionBarSearch from '@/components/Interface/DataTable/ActionBar/ActionBarSearch.vue'
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue'
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue'
import { useMapStore } from '@/stores/mapStore.ts'
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue'
import HeaderFixedWidthTextNoSort from '@/components/Interface/DataTable/Headers/HeaderFixedWidthTextNoSort.vue'
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue'
import { useFleetStore } from '@/stores/fleetStore.ts'
import { useShipStore } from '@/stores/shipStore.ts'
import { useShipActionStore } from '@/stores/shipActionStore.ts'
import type Fleet from '@/types/Fleet.ts'
import PercentageText from '@/components/Interface/Text/PercentageText.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import FleetAction from '@/components/Interface/ShipFleet/FleetAction.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const tableStore = useTableStore()
const fleetStore = useFleetStore()
const mapStore = useMapStore()
const shipStore = useShipStore()
const shipActionStore = useShipActionStore()

//Load filtered fleet list
const filters = (fleets: Fleet[]) => {
  return fleets.filter((fleet) => {
    // If no search query, return all
    if (!tableStore.fleets.filters.nameSearch || tableStore.fleets.filters.nameSearch === '') {
      return true
    }
    // Otherwise, filter based on the name (case-insensitive search)
    return fleet.name.toLowerCase().includes(tableStore.fleets.filters.nameSearch.toLowerCase())
  })
}

const onClick = (id: number) => {
  const fleet = fleetStore.fleets.get(id)
  if (fleet) {
    mapStore.focusMap(fleet.xPos, fleet.yPos)
  }
}

const onDoubleClick = (id: number) => {
  const fleet = fleetStore.fleets.get(id)
  if (fleet) {
    shipActionStore.initFleet(fleet)
  }
}
</script>
false

<template>
  <FerionDataTable
    tableStoreName="fleets"
    :selector="false"
    :filters="filters"
    :heightLimit="'h-[calc(100vh-9rem)]'"
    :data="fleetStore.fleets"
    :click="onClick"
    :double-click="onDoubleClick"
  >
    <!--- ActionBar --->
    <template #actionBar>
      <ActionBarFiller class="mr-1 rounded">
        <div class="py-3">&nbsp;</div>
      </ActionBarFiller>
      <ActionBarSearch
        v-model="tableStore.fleets.filters.nameSearch"
        :name="t('map.sidebar.fleets.name')"
        :placeHolder="t('map.sidebar.fleets.search')"
      />
    </template>
    <!--- Headers --->
    <template #columns="{ sort, sortColumn, sortDirection }">
      <HeaderTextFlex
        min-width="12"
        column="name"
        :sortColumn="sortColumn"
        :sortDirection="sortDirection"
        @sort="sort"
        class="rounded-tl pl-2"
      >
        {{ t('map.sidebar.fleets.name') }}
      </HeaderTextFlex>
      <HeaderFixedWidthText
        width="3"
        align="center"
        column="healthPercentage"
        :sortColumn="sortColumn"
        :sortDirection="sortDirection"
        @sort="sort"
      >
        <GenericIcon :tooltip="t('command.fleets.tooltips.hitPoints')" type="hit_points" />
      </HeaderFixedWidthText>
      <HeaderFixedWidthTextNoSort width="3" align="center"> {{ t('map.sidebar.fleets.core') }} </HeaderFixedWidthTextNoSort>
      <HeaderFixedWidthTextNoSort width="3" align="center">
        <span :title="t('map.sidebar.fleets.reinforcements')">{{ t('map.sidebar.fleets.reinforcementsShort') }}</span>
      </HeaderFixedWidthTextNoSort>
      <HeaderFixedWidthText
        width="10"
        column="currentActionName"
        :sortColumn="sortColumn"
        :sortDirection="sortDirection"
        @sort="sort"
        align="center"
        class="rounded-tr"
      >
        {{ t('map.sidebar.fleets.action') }}
      </HeaderFixedWidthText>
    </template>
    <!--- Rows --->
    <template #rows="{ item }">
      <CellFlexWidth min-width="12" class="pl-2">
        <div class="flex-col">
          <div>{{ item.name }}</div>
        </div>
      </CellFlexWidth>
      <CellFixedWidth width="3" align="center">
        <PercentageText :value="item.healthPercentage"></PercentageText>
      </CellFixedWidth>
      <CellFixedWidth width="3" align="center">
        <span class="text-subtext">{{ shipStore.countShipsInFleet(item.id) }}</span>
      </CellFixedWidth>
      <CellFixedWidth width="3" align="center">
        <span class="text-subtext">{{ shipStore.countShipsInFleetCatchingUp(item.id) }}</span>
      </CellFixedWidth>
      <CellFixedWidth width="10" align="center">
        <FleetAction :fleetId="item.id" />
      </CellFixedWidth>
    </template>
  </FerionDataTable>
</template>

<style scoped lang="scss"></style>
